"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import WorkshopHero from "../../src/components/workshops/Hero";
import ProgramOutcomesSection from "../../src/components/mentorship/ProgramOutcomesSection";
import SuccessStories from "../../src/components/home/SuccessStories";
import CompaniesSection from "../../src/components/home/CompaniesSection";
import TestimonialsSection from "../../src/components/home/TestimonialsSection";
import Business from "../../src/components/home/Business";
import WorkshopStats from "../../src/components/workshops/Stats";
import StickyBanner from "../../src/components/workshops/Sticky";
import workshopsData from "../../src/data/workshops.json";
import mentorsDataJson from "../../src/data/mentors.json";

export default function WorkshopPage({ initialWorkshop, initialMentors = [] }) {
  const router = useRouter();
  const { slug } = router.query;

  const [workshop, setWorkshop] = useState(initialWorkshop || null);
  const [mentors, setMentors] = useState(initialMentors || []);
  const [loading] = useState(false);
  const [error] = useState(null);
  const [notFound] = useState(!initialWorkshop);

  // Redirect linkedin-growth to external URL and chatgpt-workshop to homepage
  useEffect(() => {
    if (slug === "linkedin-growth") {
      window.location.href = "https://ws.growthschool.io/linkedin-5d";
    } else if (slug === "chatgpt-workshop") {
      router.push("/");
    }
  }, [slug, router]);

  // Early return for redirects
  if (slug === "linkedin-growth" || slug === "chatgpt-workshop") {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002726] mx-auto mb-4"></div>
          <p className="text-[#002726] text-lg">Redirecting...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#002726] mx-auto mb-4"></div>
          <p className="text-[#002726] text-lg">Loading workshop...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg mb-4">
            Error loading workshop: {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#002726] text-white px-4 py-2 rounded-lg hover:bg-[#004726] transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#002726] mb-4">
            Workshop Not Found
          </h1>
          <p className="text-gray-600 mb-4">
            The workshop you&apos;re looking for doesn&apos;t exist.
          </p>
          <button
            onClick={() => router.push("/")}
            className="bg-[#002726] text-white px-4 py-2 rounded-lg hover:bg-[#004726] transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{workshop.heading} | GrowthSchool</title>
        <meta name="description" content={workshop.subheading} />
      </Head>

      <div>
        <WorkshopHero workshop={workshop} mentors={mentors} />
        <WorkshopStats />
        <ProgramOutcomesSection mentors={mentors} />
        <SuccessStories />
        <CompaniesSection />
        <TestimonialsSection />
        <Business />
        <StickyBanner workshop={workshop} />
      </div>
    </>
  );
}

export async function getStaticPaths() {
  const workshops = require("../../src/data/workshops.json");
  const paths = (workshops || []).map((w) => ({ params: { slug: w.slug } }));
  return { paths, fallback: false };
}

export async function getStaticProps({ params }) {
  const workshops = require("../../src/data/workshops.json");
  const mentors = require("../../src/data/mentors.json");
  
  // For linkedin-growth and chatgpt-workshop, return null workshop so client-side redirect can handle it
  // (Static export doesn't support redirects in getStaticProps)
  if (params.slug === "linkedin-growth" || params.slug === "chatgpt-workshop") {
    return {
      props: {
        initialWorkshop: null,
        initialMentors: mentors || [],
      },
    };
  }

  const workshop =
    (workshops || []).find((w) => w.slug === params.slug) || null;

  return {
    props: {
      initialWorkshop: workshop,
      initialMentors: mentors || [],
    },
  };
}
